// ---------------------------------------------------------------------------
// menuConfigApi — backend-persisted platform menu config (v0.17.0)
//
// Replaces the localStorage-only override approach with a single backend
// resource at /api/v1/platform/menu-config/. The backend stores ONE row
// platform-wide; only platform_admin can write, all authenticated users
// read.
//
// Payload shape:
//   {
//     version: 1,
//     shells: {
//       admin?:  { main: NavItemSerialized[], bottom: NavItemSerialized[] },
//       ops?:    { main: ..., bottom: ... },
//       client?: { main: ..., bottom: ... },
//     },
//     user_routes: UserRouteSerialized[],
//     updated_at: string,
//   }
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  NavItemSerialized,
  NavOverrideShell,
  ShellKey,
} from "./navOverride";
import type { UserRoute } from "./userRoutes";

export const MENU_CONFIG_KEY = ["platform-menu-config"] as const;

export interface MenuConfigPayload {
  version?: 1;
  shells?: Partial<Record<ShellKey, NavOverrideShell>>;
  user_routes?: UserRoute[];
}

interface MenuConfigEnvelopeData {
  payload: MenuConfigPayload;
  updated_at: string;
  updated_by_username: string | null;
}

interface ApiEnvelope<T> {
  status: "success" | "error";
  data: T;
  meta?: Record<string, unknown>;
}

/** Fetch the current platform menu config. Empty payload = use defaults. */
async function fetchMenuConfig(): Promise<MenuConfigEnvelopeData> {
  const response = await api.get<ApiEnvelope<MenuConfigEnvelopeData>>(
    "/platform/menu-config/",
  );
  return response.data.data;
}

/** Replace the platform menu config payload (platform_admin only). */
async function putMenuConfig(
  payload: MenuConfigPayload,
): Promise<MenuConfigEnvelopeData> {
  const response = await api.put<ApiEnvelope<MenuConfigEnvelopeData>>(
    "/platform/menu-config/",
    { payload },
  );
  return response.data.data;
}

/** Reset the platform menu config to default (platform_admin only). */
async function deleteMenuConfig(): Promise<MenuConfigEnvelopeData> {
  const response = await api.delete<ApiEnvelope<MenuConfigEnvelopeData>>(
    "/platform/menu-config/",
  );
  return response.data.data;
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export function useMenuConfig() {
  return useQuery({
    queryKey: MENU_CONFIG_KEY,
    queryFn: fetchMenuConfig,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSaveMenuConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putMenuConfig,
    onSuccess: (data) => {
      qc.setQueryData(MENU_CONFIG_KEY, data);
    },
  });
}

export function useResetMenuConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteMenuConfig,
    onSuccess: (data) => {
      qc.setQueryData(MENU_CONFIG_KEY, data);
    },
  });
}

// ---------------------------------------------------------------------------
// Imperative helpers — used by code outside react components (e.g. the
// editor's seed function during initialization). These bypass the cache.
// ---------------------------------------------------------------------------

export async function loadMenuConfigPayload(): Promise<MenuConfigPayload> {
  try {
    const data = await fetchMenuConfig();
    return data.payload ?? {};
  } catch {
    return {};
  }
}

export function shellOverrideFromPayload(
  payload: MenuConfigPayload,
  shell: ShellKey,
): NavOverrideShell | null {
  return payload.shells?.[shell] ?? null;
}

export function userRoutesFromPayload(payload: MenuConfigPayload): UserRoute[] {
  return payload.user_routes ?? [];
}

export function buildPayloadWithShellOverride(
  current: MenuConfigPayload,
  shell: ShellKey,
  override: NavOverrideShell,
): MenuConfigPayload {
  return {
    ...current,
    version: 1,
    shells: { ...(current.shells ?? {}), [shell]: override },
  };
}

export function buildPayloadWithoutShellOverride(
  current: MenuConfigPayload,
  shell: ShellKey,
): MenuConfigPayload {
  const shells = { ...(current.shells ?? {}) };
  delete shells[shell];
  return { ...current, version: 1, shells };
}

export function buildPayloadWithUserRoutes(
  current: MenuConfigPayload,
  routes: UserRoute[],
): MenuConfigPayload {
  return { ...current, version: 1, user_routes: routes };
}

/**
 * Re-export the shape of NavItemSerialized so consumers don't need a
 * second import path. (Type only — no runtime cost.)
 */
export type { NavItemSerialized };
